/* Twilio Voice connection card — sits below TwilioCard on Settings page.
 *
 * Requires the user to:
 *  1. Already have an SMS connection (Account SID + Auth Token + From-Number).
 *  2. Create a Twilio API Key (SK…) in their console — Account → API keys & tokens.
 *  3. Create a TwiML App (AP…) in their console — Voice → TwiML Apps. Set the
 *     Voice Request URL to {backend}/api/voice/twiml/outbound, method POST.
 *  4. On their voice-enabled phone number, set "A Call Comes In" →
 *     {backend}/api/voice/twiml/inbound, POST.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  PhoneCall, Loader2, CheckCircle2, ExternalLink, Trash2, KeyRound,
  Eye, EyeOff, Mic, Copy,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VoiceCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [form, setForm] = useState({
    api_key_sid: "",
    api_key_secret: "",
    twiml_app_sid: "",
    disclaimer_enabled: true,
    disclaimer_text: "This call may be recorded for quality and training purposes.",
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/voice/status");
        if (alive) {
          setStatus(data);
          if (data.voice_disclaimer_text) {
            setForm((f) => ({
              ...f,
              disclaimer_enabled: data.voice_disclaimer_enabled,
              disclaimer_text: data.voice_disclaimer_text,
            }));
          }
        }
      } catch {
        if (alive) setStatus({ connected: false, voice_connected: false });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/voice/connect-voice", form);
      setStatus(data);
      setOpen(false);
      setForm((f) => ({ ...f, api_key_sid: "", api_key_secret: "", twiml_app_sid: "" }));
      toast.success("Voice connected — click-to-call ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice connection failed");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Twilio Voice? Click-to-call and recording will stop.")) return;
    setBusy(true);
    try {
      await api.delete("/voice/disconnect-voice");
      const { data } = await api.get("/voice/status");
      setStatus(data);
      toast.success("Voice disconnected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleDisclaimer = async () => {
    const next = !status.voice_disclaimer_enabled;
    try {
      const { data } = await api.patch("/voice/settings", { disclaimer_enabled: next });
      setStatus(data);
      toast.success(next ? "Disclaimer will play before recording" : "Disclaimer disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const saveDisclaimerText = async () => {
    try {
      const { data } = await api.patch("/voice/settings", { disclaimer_text: form.disclaimer_text });
      setStatus(data);
      toast.success("Disclaimer text saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(url);
    toast.success("URL copied");
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[var(--line)] p-5">
        <Loader2 size={16} className="animate-spin inline mr-2" /> Loading Voice…
      </div>
    );
  }

  // Show pre-req warning if no SMS connection yet.
  if (!status?.connected) {
    return (
      <div className="bg-white rounded-xl border border-[var(--line)] p-5" data-testid="voice-card">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-center shrink-0">
            <PhoneCall size={18} className="text-indigo-600" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-extrabold text-lg leading-tight">Click-to-Call &amp; Recording</div>
            <div className="text-xs text-[var(--ink-2)] mt-0.5">
              Connect your Twilio SMS account first (above), then come back to enable browser-based dialing.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const outboundUrl = `${BACKEND_URL}/api/voice/twiml/outbound`;
  const inboundUrl = `${BACKEND_URL}/api/voice/twiml/inbound`;

  return (
    <div id="voice-card" className="bg-white rounded-xl border border-[var(--line)] p-5" data-testid="voice-card">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-center shrink-0">
            <PhoneCall size={18} className="text-indigo-600" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-extrabold text-lg leading-tight">Click-to-Call &amp; Recording</div>
            <div className="text-xs text-[var(--ink-2)] mt-0.5">
              Dial sellers &amp; buyers from inside the app. Calls auto-record on your Twilio account.
            </div>
          </div>
        </div>
        {status.voice_connected && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 shrink-0">
            <CheckCircle2 size={10} /> Voice ready
          </span>
        )}
      </div>

      {status.voice_connected ? (
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold">API Key</div>
              <div className="font-mono text-[11px] mt-0.5 truncate">{status.voice_api_key_sid}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold">TwiML App</div>
              <div className="font-mono text-[11px] mt-0.5 truncate">{status.voice_twiml_app_sid}</div>
            </div>
          </div>

          {/* Disclaimer toggle */}
          <div className="border-t border-[var(--line)] pt-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold flex items-center gap-1.5">
                  <Mic size={12} /> Pre-call recording disclaimer
                </div>
                <div className="text-[11px] text-[var(--ink-2)] mt-0.5">
                  Required in 2-party-consent states (CA, FL, PA, IL, MA, MD, MT, NH, WA).
                </div>
              </div>
              <button
                type="button"
                onClick={toggleDisclaimer}
                className={`relative w-10 h-6 rounded-full transition shrink-0 ${
                  status.voice_disclaimer_enabled ? "bg-emerald-500" : "bg-slate-300"
                }`}
                data-testid="voice-disclaimer-toggle"
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition ${
                  status.voice_disclaimer_enabled ? "left-[18px]" : "left-0.5"
                }`} />
              </button>
            </div>
            {status.voice_disclaimer_enabled && (
              <div className="mt-2 flex gap-2">
                <Input
                  className="text-xs flex-1"
                  value={form.disclaimer_text}
                  onChange={(e) => setForm((f) => ({ ...f, disclaimer_text: e.target.value }))}
                  data-testid="voice-disclaimer-text"
                />
                <Button size="sm" variant="outline" onClick={saveDisclaimerText} data-testid="voice-disclaimer-save">
                  Save
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end pt-3 border-t border-[var(--line)]">
            <Button variant="outline" size="sm" onClick={disconnect} disabled={busy} data-testid="voice-disconnect">
              <Trash2 size={12} className="mr-1.5" /> Disconnect Voice
            </Button>
          </div>
        </div>
      ) : open ? (
        <div className="space-y-3 mt-4">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900 space-y-1.5">
            <div className="font-bold">One-time Twilio Console setup:</div>
            <ol className="list-decimal list-inside space-y-1">
              <li>
                Create an <a className="underline" href="https://console.twilio.com/us1/account/keys-credentials/api-keys" target="_blank" rel="noopener noreferrer">API Key</a> — copy the SK… SID and the one-time Secret.
              </li>
              <li>
                Create a <a className="underline" href="https://console.twilio.com/us1/develop/voice/manage/twiml-apps" target="_blank" rel="noopener noreferrer">TwiML App</a> with Voice Request URL:
                <div className="flex items-center gap-1.5 mt-1">
                  <code className="bg-white px-1.5 py-0.5 rounded text-[10px] font-mono break-all flex-1">{outboundUrl}</code>
                  <button type="button" onClick={() => copyUrl(outboundUrl)} className="text-amber-700 hover:text-amber-900"><Copy size={12} /></button>
                </div>
              </li>
              <li>
                On your phone number ({status.from_number}), set &ldquo;A Call Comes In&rdquo;:
                <div className="flex items-center gap-1.5 mt-1">
                  <code className="bg-white px-1.5 py-0.5 rounded text-[10px] font-mono break-all flex-1">{inboundUrl}</code>
                  <button type="button" onClick={() => copyUrl(inboundUrl)} className="text-amber-700 hover:text-amber-900"><Copy size={12} /></button>
                </div>
              </li>
            </ol>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ink-2)] font-bold">API Key SID</Label>
            <Input
              className="font-mono text-xs"
              placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={form.api_key_sid}
              onChange={(e) => setForm((f) => ({ ...f, api_key_sid: e.target.value.trim() }))}
              data-testid="voice-api-key-sid"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ink-2)] font-bold">API Key Secret</Label>
            <div className="relative">
              <Input
                className="font-mono text-xs pr-9"
                type={showSecret ? "text" : "password"}
                placeholder="One-time secret from API Key creation"
                value={form.api_key_secret}
                onChange={(e) => setForm((f) => ({ ...f, api_key_secret: e.target.value.trim() }))}
                data-testid="voice-api-key-secret"
              />
              <button
                type="button"
                onClick={() => setShowSecret((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--ink-2)] hover:text-[var(--ink)]"
              >
                {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ink-2)] font-bold">TwiML App SID</Label>
            <Input
              className="font-mono text-xs"
              placeholder="APxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={form.twiml_app_sid}
              onChange={(e) => setForm((f) => ({ ...f, twiml_app_sid: e.target.value.trim() }))}
              data-testid="voice-twiml-app-sid"
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={busy} data-testid="voice-cancel-btn">
              Cancel
            </Button>
            <button
              type="button"
              onClick={connect}
              disabled={busy || !form.api_key_sid || !form.api_key_secret || !form.twiml_app_sid}
              data-testid="voice-connect-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold transition disabled:bg-emerald-300 disabled:text-white disabled:cursor-not-allowed"
            >
              {busy ? <><Loader2 size={14} className="animate-spin" /> Connecting…</> : <><KeyRound size={14} /> Connect Voice</>}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <Button onClick={() => setOpen(true)} className="w-full" data-testid="voice-connect-btn">
            <KeyRound size={14} className="mr-1.5" /> Enable Click-to-Call &amp; Recording
          </Button>
          <a
            href="https://console.twilio.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-[var(--ink-2)] hover:text-emerald-600 mt-2"
          >
            Open Twilio Console <ExternalLink size={10} />
          </a>
        </div>
      )}
    </div>
  );
}
